import { useState } from 'react'
import { RiArrowUpSLine, RiArrowDownSLine } from '@remixicon/react'
import PackageIcon from 'assets/package.png'
import SorterChuteStateChip from 'orders/presentation/components/atoms/SorterChuteStateChip/SorterChuteStateChip'
import { AppTable } from 'components/molecules/AppTable/AppTable'
import { SorterRuleView } from 'orders/infrastructure/adapters/SorterOrderAdapter'
import OrderSummaryPill from 'orders/presentation/components/molecules/OrderSummaryPill/OrderSummaryPill'
import { RuleIndexBadge } from 'components/atoms/RuleIndexBadge/RuleIndexBadge'

interface Props {
    ruleView: SorterRuleView
}

const MAX_BADGES = 8

const TABLE_HEADERS = [
    { id: 'chute', title: 'Chute', className: 'text-left px-6 py-3 text-xs font-medium text-[#64748B] w-[100px]' },
    { id: 'container', title: 'Contenedor', className: 'text-center px-6 py-3 text-xs font-medium text-[#64748B] w-[316px]' },
    { id: 'state', title: 'Estado operativo', className: 'text-center px-6 py-3 text-xs font-medium text-[#64748B] w-[316px]' },
    { id: 'packages', title: 'Paquetes', className: 'text-center px-6 py-3 text-xs font-medium text-[#64748B] w-[316px]' },
]

export default function SorterGroupAccordion({ ruleView }: Props) {
    const [open, setOpen] = useState(true)
    const { name, sequence, chutes, chuteNumbers, totalPackages } = ruleView
    const hasOverflow = chuteNumbers.length > MAX_BADGES
    const visibleChuteNumbers = hasOverflow ? chuteNumbers.slice(0, MAX_BADGES - 1) : chuteNumbers

    return (
        <div className="rounded-xl overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between px-5 py-3 bg-white"
                aria-expanded={open}
            >
                <div className="flex items-center gap-2">
                    {open
                        ? <RiArrowUpSLine size={18} className="text-[#64748B]" />
                        : <RiArrowDownSLine size={18} className="text-[#64748B]" />
                    }
                    <span className="text-sm font-semibold text-[#1E293B] uppercase tracking-wide">
                        {name}
                    </span>
                    {sequence != null && <RuleIndexBadge index={sequence} />}
                </div>

                <div className="flex items-center gap-2">
                    {visibleChuteNumbers.map((num) => (
                        <span
                            key={num}
                            className="min-w-[32px] h-8 flex items-center justify-center rounded-lg bg-[#F0F9EB] text-sm font-semibold text-[#3D6B2C] px-2"
                        >
                            {num}
                        </span>
                    ))}
                    {hasOverflow && (
                        <span className="min-w-[32px] h-8 flex items-center justify-center rounded-lg bg-[#F0F9EB] text-sm font-semibold text-[#3D6B2C] px-2">
                            +
                        </span>
                    )}
                    <div className="flex items-center gap-1.5 ml-2">
                        <img src={PackageIcon} alt="paquetes" className="w-5 h-5" />
                        <span className="text-2xl font-semibold leading-7 text-right text-[#475569]">{totalPackages}</span>
                    </div>
                </div>
            </button>

            {open && (
                <div className="px-3 pb-3 bg-white">
                    <AppTable dataHead={TABLE_HEADERS}>
                        <tbody>
                            {chutes.map((chute) => (
                                chute.containers.length <= 1 ? (
                                    <tr key={chute.chuteNumber}>
                                        <td className="px-6 py-4 border-t border-[#E2E8F0]">
                                            <span className="text-2xl font-bold leading-none text-[#1E293B]">
                                                {String(chute.chuteNumber).padStart(2, '0')}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 border-t border-[#E2E8F0] text-center">
                                            {chute.containers[0] ? (
                                                <OrderSummaryPill containerCode={chute.containers[0].code} centered />
                                            ) : (
                                                <span className="text-sm text-[#CBD5E1]">—</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 border-t border-[#E2E8F0] text-center">
                                            <SorterChuteStateChip state={chute.containers[0]?.status ?? null} />
                                        </td>
                                        <td className="px-6 py-4 border-t border-[#E2E8F0] text-center">
                                            <span className="text-base font-semibold text-[#1E293B]">
                                                {chute.containers[0]?.packageCount ?? chute.packageCount}
                                            </span>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={chute.chuteNumber}>
                                        <td className="px-6 py-4 border-t border-[#E2E8F0] align-middle">
                                            <span className="text-2xl font-bold leading-none text-[#1E293B]">
                                                {String(chute.chuteNumber).padStart(2, '0')}
                                            </span>
                                        </td>
                                        <td className="px-6 py-2 border-t border-[#E2E8F0] text-center">
                                            <div className="flex flex-col gap-2 items-center">
                                                {chute.containers.map((container, idx) => (
                                                    <OrderSummaryPill key={idx} containerCode={container.code} centered />
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-6 py-2 border-t border-[#E2E8F0] text-center">
                                            <div className="flex flex-col gap-2 items-center">
                                                {chute.containers.map((container, idx) => (
                                                    <div key={idx} className="flex items-center justify-center h-[28px]">
                                                        <SorterChuteStateChip state={container.status} />
                                                    </div>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-6 py-2 border-t border-[#E2E8F0] text-center">
                                            <div className="flex flex-col gap-2 items-center">
                                                {chute.containers.map((container, idx) => (
                                                    <span key={idx} className="text-base font-semibold text-[#1E293B] h-[28px] flex items-center justify-center">
                                                        {container.packageCount}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                )
                            ))}
                            {chutes.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-6 py-6 text-center text-sm text-[#94A3B8] border-t border-[#E2E8F0]">
                                        Sin chutes asignados
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </AppTable>
                </div>
            )}
        </div>
    )
}
